/**
 * Browser-side image compression + thumbnail generation.
 *
 * Why this lives in core: feed image attachments, profile avatars, and chat
 * uploads all benefit from the same WebP pipeline. Compressing in the
 * browser caps storage cost before bytes ever leave the device — phone
 * photos routinely arrive at 4-12MB and shrink to 200-600KB with no visible
 * quality loss at any UI surface we render to.
 *
 * Skips: GIFs (would freeze on first frame), tiny files (CPU waste), SVG
 * or other non-raster types. On decode/OOM/codec failure we return the
 * original file rather than block the send.
 */

const MAX_DIMENSION = 1920;
const THUMB_DIMENSION = 256;
const AVATAR_MAX_DIMENSION = 512;
const TARGET_TYPE = "image/webp";
const TARGET_QUALITY = 0.82;
const THUMB_QUALITY = 0.72;
const SKIP_BELOW_BYTES = 200 * 1024;

export interface PreparedImage {
  file: File;
  width: number;
  height: number;
  /** Optional small thumbnail (~256px). Absent for tiny inputs / GIFs. */
  thumb?: { file: File; width: number; height: number };
}

function isBrowser(): boolean {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

async function decodeToBitmap(file: File): Promise<ImageBitmap> {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file);
    } catch {
      // some Safari versions fail on heic/avif — fall through
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error("decode failed"));
      el.src = url;
    });
    return await createImageBitmap(img);
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function encodeVariant(
  bitmap: ImageBitmap,
  maxDim: number,
  quality: number,
  baseName: string
): Promise<{ file: File; width: number; height: number } | null> {
  const scale = Math.min(1, maxDim / bitmap.width, maxDim / bitmap.height);
  const w = Math.round(bitmap.width * scale);
  const h = Math.round(bitmap.height * scale);

  let canvas: HTMLCanvasElement | OffscreenCanvas;
  if (typeof OffscreenCanvas !== "undefined") {
    canvas = new OffscreenCanvas(w, h);
  } else {
    canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
  }
  const ctx = canvas.getContext("2d") as
    | CanvasRenderingContext2D
    | OffscreenCanvasRenderingContext2D
    | null;
  if (!ctx) return null;
  ctx.drawImage(bitmap, 0, 0, w, h);

  let blob: Blob | null = null;
  try {
    if (canvas instanceof OffscreenCanvas) {
      blob = await canvas.convertToBlob({ type: TARGET_TYPE, quality });
    } else {
      blob = await new Promise<Blob | null>((resolve) =>
        (canvas as HTMLCanvasElement).toBlob(resolve, TARGET_TYPE, quality)
      );
    }
  } catch {
    blob = null;
  }
  if (!blob) return null;

  const file = new File([blob], baseName, { type: TARGET_TYPE });
  return { file, width: w, height: h };
}

export async function prepareImageUpload(
  input: File,
  opts: { withThumb?: boolean; maxDimension?: number; quality?: number } = {}
): Promise<PreparedImage> {
  if (!isBrowser()) return { file: input, width: 0, height: 0 };
  if (input.type === "image/gif") {
    const bmp = await decodeToBitmap(input).catch(() => null);
    return { file: input, width: bmp?.width ?? 0, height: bmp?.height ?? 0 };
  }
  if (!input.type.startsWith("image/")) {
    return { file: input, width: 0, height: 0 };
  }

  let bitmap: ImageBitmap;
  try {
    bitmap = await decodeToBitmap(input);
  } catch {
    return { file: input, width: 0, height: 0 };
  }

  const baseName = input.name.replace(/\.[^.]+$/, "");
  const maxDim = opts.maxDimension ?? MAX_DIMENSION;
  const quality = opts.quality ?? TARGET_QUALITY;

  let preview: { file: File; width: number; height: number };
  if (input.size < SKIP_BELOW_BYTES && bitmap.width <= maxDim && bitmap.height <= maxDim) {
    preview = { file: input, width: bitmap.width, height: bitmap.height };
  } else {
    const encoded = await encodeVariant(bitmap, maxDim, quality, `${baseName}.webp`);
    if (!encoded) {
      bitmap.close?.();
      return { file: input, width: bitmap.width, height: bitmap.height };
    }
    preview = encoded.file.size >= input.size
      ? { file: input, width: encoded.width, height: encoded.height }
      : encoded;
  }

  let thumb: { file: File; width: number; height: number } | undefined;
  if (opts.withThumb && Math.max(bitmap.width, bitmap.height) > THUMB_DIMENSION) {
    const t = await encodeVariant(bitmap, THUMB_DIMENSION, THUMB_QUALITY, `${baseName}-thumb.webp`);
    if (t) thumb = t;
  }
  bitmap.close?.();

  return { file: preview.file, width: preview.width, height: preview.height, thumb };
}

/** Avatar: cap at 512px (we never render bigger than ~80px). */
export async function prepareAvatarUpload(input: File): Promise<PreparedImage> {
  return prepareImageUpload(input, {
    maxDimension: AVATAR_MAX_DIMENSION,
    quality: 0.85,
    withThumb: false,
  });
}

/** Feed: cap at 1920px + always generate a 256px thumbnail. */
export async function prepareFeedImageUpload(input: File): Promise<PreparedImage> {
  return prepareImageUpload(input, { withThumb: true });
}
